/**
 * Utility functions for transforming user form data to API request formats.
 */

import type { CreateUserRequest, UpdateUserRequest } from '@/lib/api';
import type { CreateUserFormData, UpdateUserFormData } from '../validation/userFormSchemas';

/**
 * Transforms create user form data to API request format.
 */
export function transformCreateUserFormData(formData: CreateUserFormData): CreateUserRequest {
  return {
    email: formData.email,
    password: formData.password,
    name: formData.name || null,
    is_admin: formData.is_admin,
    expiry_date: formData.expiry_date ? new Date(formData.expiry_date).toISOString() : null,
  };
}

/**
 * Transforms update user form data to API request format.
 */
export function transformUpdateUserFormData(formData: UpdateUserFormData): UpdateUserRequest {
  const updateData: UpdateUserRequest = {
    name: formData.name || null,
    is_admin: formData.is_admin,
    expiry_date: formData.expiry_date ? new Date(formData.expiry_date).toISOString() : null,
  };

  // Only include password if it's been provided
  if (formData.password && formData.password.trim()) {
    updateData.password = formData.password;
  } else {
    // Explicitly set to null if not provided (API expects null, not undefined)
    updateData.password = null;
  }

  return updateData;
}

/**
 * Type guard to check if form data is for creating a user.
 */
export function isCreateUserFormData(data: CreateUserFormData | UpdateUserFormData): data is CreateUserFormData {
  return 'password' in data && typeof data.password === 'string' && data.password.length > 0;
}
